import {
  type CyberScoreResult,
  type DecisionReliefMeasures,
  type ScoreBand,
  type ScoreChange,
  type ScoreTrace,
  type EvidenceGrade,
} from '@cyberscore/contracts';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { useSession, can } from '../../lib/session';

/**
 * Insurer staff and brokers see clients and the portfolio (brokers only the assessments they started or
 * joined); the configuration roles see them read-only (task 60).
 */

export function useSeesPortfolio() {
  const { tenant } = useSession();
  // A business on its own has its own pages, not an insurer's portfolio (decision 0034).
  return !!tenant && tenant.kind !== 'platform' && tenant.kind !== 'business' && can(tenant, 'portfolio.view', 'clients.view'); // prettier-ignore
}

/** Whether the platform asks less of people (task 114): checks finished, time to a score, answers taken together. */
export function useMeasures(days: number) {
  const { tenant } = useSession();
  return useQuery({
    queryKey: ['portfolio', tenant?.id, 'measures', days],
    queryFn: () => api<DecisionReliefMeasures>('GET', `/portfolio/measures?days=${days}`),
    enabled: !!tenant && can(tenant, 'portfolio.view'),
  });
}

export interface StoredScoreRow {
  seq: string;
  score: number;
  band: ScoreBand;
  provisional: boolean;
  kill_switch_cap: number | null;
  created_at: string;
}

export interface ScoreView {
  assessment_id: string;
  /** False when something changed since the last stored score, e.g. a date rule as days pass. */
  stored: boolean;
  /** The loss model is null for people who aren't staff. */
  score: CyberScoreResult;
  explanation: ScoreTrace;
  /** How far the answers are backed, A to E, with why (task 63). */
  grade: EvidenceGrade;
  history: StoredScoreRow[];
}

export function useScore(id: string) {
  const { tenant } = useSession();
  return useQuery({
    queryKey: ['assessments', tenant?.id, id, 'score'],
    queryFn: () => api<ScoreView>('GET', `/assessments/${id}/score`),
    enabled: !!tenant && !!id,
  });
}

export interface ScoreMove {
  from: StoredScoreRow | null;
  to: StoredScoreRow;
  change: ScoreChange | null;
}

export function useScoreChange(id: string, seq: string | null) {
  const { tenant } = useSession();
  return useQuery({
    queryKey: ['assessments', tenant?.id, id, 'score-change', seq],
    queryFn: () => api<ScoreMove>('GET', `/assessments/${id}/scores/${seq}/change`),
    enabled: !!tenant && !!seq,
  });
}
